/**
 * 发版后旧页面「点菜单没反应」的兜底：代码块加载失败就整页刷到目标地址。
 *
 * 现象（2026-09-14 dev 走查）：客服点侧栏「汇率设置」没任何反应，刷新后正常。
 * 原因：每个页面的代码是点击时才按需加载的，文件名带构建哈希；页面是发版前打开的，
 * 记的是旧文件名，发版后服务器上只剩新文件，请求 404。vue-router 懒加载失败会直接放弃
 * 这次跳转，既不报错也不提示，看起来就是「没反应」。dev 一天发好几版，必然反复撞上。
 *
 * 处理：捕获这类错误，整页跳到本来要去的地址 —— 等价于「刷新再点一次」，使用者感知不到。
 *   1. `router.onError`：路由级懒加载失败。知道要去哪，直接跳目标地址。
 *   2. `vite:preloadError`：Vite 打包后所有 `import()` 失败都会先派发这个事件（含预加载 CSS、
 *      非路由的 `defineAsyncComponent` / `import('xlsx')` 这类），刷当前地址。
 *      路由跳转进行中时不接管、让它照常抛给 1 处理——否则会刷回旧页而不是目标页。
 *
 * 防死循环：10 秒内只自动刷一次。刷了还失败（文件真没了 / 断网 / 代码本身抛错）就不再刷，
 * 让错误照常抛出。sessionStorage 写不进（旧 Safari 隐私模式）时同样不刷 —— 宁可不刷也不能刷成死循环。
 *
 * 只在打包产物里起作用：dev server 不切块、也没有 vite:preloadError 事件；index.html 在 nginx 里是 no-store，
 * 整页跳过去一定拿到新的文件名清单。三个前端应用各放一份同样的文件（不做 monorepo，CLAUDE.md §1）。
 */
import type { RouteLocationNormalized, Router } from 'vue-router'

const STAMP_KEY = 'zr:chunk-reload-at'
const ONCE_WINDOW_MS = 10_000

/** Chrome / Firefox / Safari 三家的动态导入失败文案各不相同，再加 Vite 自己的 CSS 预加载失败 */
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload CSS/i

export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return CHUNK_ERROR_RE.test(msg)
}

/** 返回 true 表示已经在跳了；false 表示被防循环拦住，调用方让错误照常走 */
function reloadOnce(href: string): boolean {
  try {
    const last = Number(sessionStorage.getItem(STAMP_KEY) ?? 0)
    if (Date.now() - last < ONCE_WINDOW_MS) return false
    sessionStorage.setItem(STAMP_KEY, String(Date.now()))
  } catch {
    return false
  }
  window.location.assign(href)
  return true
}

export function installChunkReload(router: Router): void {
  // 正在进行的跳转。vue-router 把同一个 to 对象一路传到 beforeEach / afterEach / onError，按引用比对即可
  let pending: RouteLocationNormalized | null = null
  router.beforeEach((to) => {
    pending = to
  })
  router.afterEach((to) => {
    if (pending === to) pending = null
  })

  router.onError((err, to) => {
    if (pending === to) pending = null
    if (isChunkLoadError(err)) reloadOnce(router.options.history.createHref(to.fullPath))
  })

  window.addEventListener('vite:preloadError', (e) => {
    if (pending || !isChunkLoadError(e.payload)) return
    if (reloadOnce(window.location.href)) e.preventDefault()
  })
}
